// src/components/statistics/PlanDistributionPanel.jsx
import React, { useState, useEffect } from 'react';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Sector
} from 'recharts';
import {
    Box,
    Paper,
    Typography,
    Switch,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Card,
    CardContent
} from '@mui/material';
import {
    PieChart as PieChartIcon,
    TrendingUp as TrendingUpIcon,
    Person as PersonIcon,
    Info as InfoIcon
} from '@mui/icons-material';

// Componente para el sector activo en el gráfico
const renderActiveShape = (props) => {
    const {
        cx, cy, innerRadius, outerRadius, startAngle, endAngle,
        fill, payload, percent, value
    } = props;

    return (
        <g>
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius + 6}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
            />
            <Sector
                cx={cx}
                cy={cy}
                startAngle={startAngle}
                endAngle={endAngle}
                innerRadius={outerRadius + 6}
                outerRadius={outerRadius + 10}
                fill={fill}
            />
            <text
                x={cx}
                y={cy}
                dy={-20}
                textAnchor="middle"
                fill="#333"
                style={{ fontWeight: 'bold', fontSize: '1rem' }}
            >
                {payload.name}
            </text>
            <text
                x={cx}
                y={cy}
                dy={8}
                textAnchor="middle"
                fill="#666"
            >
                {`${value} clientes`}
            </text>
            <text
                x={cx}
                y={cy}
                dy={25}
                textAnchor="middle"
                fill="#999"
            >
                {`(${(percent * 100).toFixed(1)}%)`}
            </text>
        </g>
    );
};

// Tooltip personalizado
const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        return (
            <Card className="p-2 border shadow-md" style={{ maxWidth: 220 }}>
                <Typography className="font-bold mb-1" style={{ color: payload[0].color }}>
                    {payload[0].name}: {payload[0].value} clientes
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                    {`Representa el ${payload[0].payload.percentage}% de clientes`}
                </Typography>
            </Card>
        );
    }
    return null;
};

const PlanDistributionPanel = ({ planDistribution = [] }) => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [showTable, setShowTable] = useState(true); // Iniciar mostrando la tabla
    const [processedData, setProcessedData] = useState([]);
    const [totalCalculated, setTotalCalculated] = useState(0);

    // Colores vibrantes personalizados
    const COLORS = [
        '#4f46e5', // Indigo
        '#06b6d4', // Cyan
        '#ef4444', // Red
        '#10b981', // Emerald
        '#f59e0b', // Amber
        '#3b82f6', // Blue
        '#8b5cf6', // Purple
        '#4CAF50', // Green
        '#FF9800', // Orange
        '#9C27B0'  // Purple
    ];

    // Procesamiento y validación de datos
    useEffect(() => {
        if (!Array.isArray(planDistribution) || planDistribution.length === 0) {
            setProcessedData([]);
            setTotalCalculated(0);
            return;
        }

        // Procesar los datos para asegurar que los valores son números
        const processed = planDistribution.map(item => ({
            ...item,
            value: parseInt(item.value, 10) || 0 // Asegurar que el valor es un número
        }));

        // Calcular el total correctamente sumando todos los values
        const calculatedTotal = processed.reduce((sum, item) => sum + item.value, 0);

        // Recalcular porcentajes basados en el total calculado
        const withPercentages = processed.map(item => ({
            ...item,
            percentage: calculatedTotal > 0
                ? parseFloat(((item.value / calculatedTotal) * 100).toFixed(1))
                : 0
        }));

        setProcessedData(withPercentages);
        setTotalCalculated(calculatedTotal);
    }, [planDistribution]);

    // Manejadores para interactividad
    const onPieEnter = (_, index) => {
        setActiveIndex(index);
    };

    const onPieLeave = () => {
        setActiveIndex(null);
    };

    return (
        <Paper
            className="h-full rounded-xl transition-all duration-300 hover:shadow-lg"
            style={{
                borderRadius: '12px',
                border: '1px solid rgba(0,0,0,0.05)',
                overflow: 'visible'
            }}
        >
            <CardContent className="p-4">
                <Box className="flex justify-between items-center mb-4">
                    <Box className="flex items-center">
                        <PieChartIcon className="text-gray-500 mr-2" />
                        <Typography variant="h6" className="font-semibold">
                            Distribución por Plan
                        </Typography>
                    </Box>
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showTable}
                                    onChange={() => setShowTable(!showTable)}
                                    size="small"
                                />
                            }
                            label={<Typography variant="caption">Ver tabla</Typography>}
                            labelPlacement="start"
                        />
                        <IconButton
                            size="small"
                            className="ml-1 bg-blue-50"
                            title="Total de clientes con planes asignados"
                        >
                            <InfoIcon fontSize="small" className="text-blue-500" />
                        </IconButton>
                    </Box>
                </Box>

                <Box className="flex flex-col h-full">
                    {!showTable ? (
                        <>
                            <Box className="h-96">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                                        <Pie
                                            activeIndex={activeIndex}
                                            activeShape={renderActiveShape}
                                            data={processedData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={80}
                                            paddingAngle={3}
                                            dataKey="value"
                                            onMouseEnter={onPieEnter}
                                            onMouseLeave={onPieLeave}
                                        >
                                            {processedData.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={COLORS[index % COLORS.length]}
                                                    stroke="#ffffff"
                                                    strokeWidth={2}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip />} />
                                        <Legend
                                            layout="horizontal"
                                            verticalAlign="bottom"
                                            align="center"
                                            formatter={(value, entry, index) => (
                                                <span style={{ color: COLORS[index % COLORS.length], fontWeight: 500 }}>
                                                    {value}
                                                </span>
                                            )}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>

                            <Box className="flex justify-center gap-4 mt-2 mb-2">
                                <Box className="flex items-center bg-indigo-50 px-3 py-1 rounded-lg">
                                    <PersonIcon className="text-indigo-500 mr-1" fontSize="small" />
                                    <Typography variant="body2">
                                        <strong>{totalCalculated}</strong> clientes con plan
                                    </Typography>
                                </Box>

                                {processedData.length > 0 && (
                                    <Box className="flex items-center bg-green-50 px-3 py-1 rounded-lg">
                                        <TrendingUpIcon className="text-green-500 mr-1" fontSize="small" />
                                        <Typography variant="body2">
                                            Plan más popular: <strong>{processedData[0]?.name}</strong>
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </>
                    ) : (
                        <TableContainer component={Paper} className="shadow-none mt-2" style={{ height: 280, overflow: 'auto' }}>
                            <Table size="small" aria-label="tabla de distribución de planes">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Plan</TableCell>
                                        <TableCell align="right">Clientes</TableCell>
                                        <TableCell align="right">Porcentaje</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {processedData.map((row, index) => (
                                        <TableRow
                                            key={row.name || `plan-${index}`}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell component="th" scope="row">
                                                <Box className="flex items-center">
                                                    <span
                                                        className="inline-block w-3 h-3 rounded-full mr-2"
                                                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                                    />
                                                    {row.name}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">{row.value}</TableCell>
                                            <TableCell align="right">{row.percentage}%</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow className="bg-indigo-50">
                                        <TableCell component="th" scope="row" className="font-bold">
                                            Total
                                        </TableCell>
                                        <TableCell align="right" className="font-bold">{totalCalculated}</TableCell>
                                        <TableCell align="right" className="font-bold">100%</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </CardContent>
        </Paper>
    );
};

export default PlanDistributionPanel;